import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Phone, Mail, Calendar, Trash2, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, Wordmark } from "@/components/site/Logo";

const STATUS = {
  new: { label: "New", cls: "bg-[#F5B841] text-black" },
  contacted: { label: "Contacted", cls: "bg-blue-500 text-white" },
  closed: { label: "Closed", cls: "bg-white/15 text-white/70" },
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get("/bookings"), api.get("/bookings/stats")]);
      setBookings(b.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success(`Marked as ${STATUS[status].label}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const shown = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const statCards = [
    { k: "total", l: "Total Requests" },
    { k: "new", l: "New" },
    { k: "contacted", l: "Contacted" },
    { k: "closed", l: "Closed" },
  ];

  return (
    <div className="min-h-screen bg-[#050505] relative z-[2]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#050505]/85 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-8 w-8" />
            <Wordmark tag={false} />
            <span className="hidden sm:inline text-white/30 text-sm ml-2">/ Bookings</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="refresh-btn" className="text-white/60 hover:text-[#F5B841] p-2 transition-colors">
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <span className="hidden sm:inline text-white/50 text-sm">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm text-white/70 hover:border-[#F5B841] hover:text-[#F5B841] transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        <h1 className="font-display text-3xl md:text-4xl tracking-tight mb-8">Booking Requests</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {statCards.map((c) => (
            <div key={c.k} className="bg-[#0a0a0a] border border-white/10 p-6" data-testid={`stat-${c.k}`}>
              <div className="font-display text-4xl text-[#F5B841] tracking-tighter">{stats[c.k]}</div>
              <div className="text-white/50 text-sm mt-2">{c.l}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {["all", "new", "contacted", "closed"].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm border capitalize transition-colors ${
                filter === f ? "bg-white text-black border-white" : "border-white/15 text-white/60 hover:border-white/40"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <div className="border border-white/10 bg-[#0a0a0a] p-16 flex flex-col items-center text-center" data-testid="empty-state">
            <Inbox className="w-12 h-12 text-white/20" />
            <p className="text-white/50 mt-4">No {filter !== "all" ? filter : ""} requests yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {shown.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                data-testid={`booking-${b.id}`}
                className="bg-[#0a0a0a] border border-white/10 p-6 grid grid-cols-1 lg:grid-cols-12 gap-4"
              >
                <div className="lg:col-span-3">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-xl tracking-tight">{b.name}</h3>
                    <span className={`text-[10px] px-2 py-0.5 tracking-wider ${STATUS[b.status]?.cls}`}>
                      {STATUS[b.status]?.label || b.status}
                    </span>
                  </div>
                  <div className="mt-3 space-y-1.5 text-sm text-white/60">
                    <a href={`tel:${b.phone}`} className="flex items-center gap-2 hover:text-[#F5B841]"><Phone className="w-3.5 h-3.5" /> {b.phone}</a>
                    <a href={`mailto:${b.email}`} className="flex items-center gap-2 hover:text-[#F5B841]"><Mail className="w-3.5 h-3.5" /> {b.email}</a>
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-[#F5B841]"><Calendar className="w-3.5 h-3.5" /> {b.event_type}</span>
                    {b.event_date && <span className="text-white/40">· {b.event_date}</span>}
                  </div>
                  {b.services?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {b.services.map((s) => (
                        <span key={s} className="text-xs border border-white/15 px-2 py-1 text-white/60">{s}</span>
                      ))}
                    </div>
                  )}
                  {b.message && <p className="text-white/50 text-sm mt-3 italic">“{b.message}”</p>}
                  <p className="text-white/25 text-xs mt-3">{new Date(b.created_at).toLocaleString()}</p>
                </div>

                <div className="lg:col-span-4 flex flex-wrap items-start gap-2 lg:justify-end">
                  {b.status !== "contacted" && (
                    <button onClick={() => changeStatus(b.id, "contacted")} data-testid={`contacted-${b.id}`} className="text-xs border border-blue-500/40 text-blue-400 px-3 py-2 hover:bg-blue-500 hover:text-white transition-colors">Mark Contacted</button>
                  )}
                  {b.status !== "closed" && (
                    <button onClick={() => changeStatus(b.id, "closed")} data-testid={`close-${b.id}`} className="text-xs border border-white/15 text-white/60 px-3 py-2 hover:bg-white hover:text-black transition-colors">Close</button>
                  )}
                  {b.status !== "new" && (
                    <button onClick={() => changeStatus(b.id, "new")} className="text-xs border border-[#F5B841]/40 text-[#F5B841] px-3 py-2 hover:bg-[#F5B841] hover:text-black transition-colors">Reopen</button>
                  )}
                  <button onClick={() => remove(b.id)} data-testid={`delete-${b.id}`} className="text-white/40 hover:text-red-400 p-2 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
